import { PINNED_ASSET_SLUG } from "@/config/game";
import { Button } from "@/design-system/Button/Button";
import { useScrollLock } from "@/hooks/useScrollLock";
import {
  Evolution,
  EvolutionState,
  EvolutionStateItem,
  evolutionsSelector,
  resetUpdateEvolutionsStatus,
  updateUserEvolutionStates,
} from "@/redux/evolutions/evolutionsSlice";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  evoStateItems,
  getEvoStateIcon,
} from "../EvolutionsOverviewPage/EvolutionsOverviewPage";
import { BaseEvoPopup } from "./BaseEvoPopup";

interface ChangeStatusPopupProps {
  onClose: () => void;
  onComplete: () => void;
  selectedEvolutions: Evolution[];
}

export const ChangeStatesPopup = ({
  onClose,
  onComplete,
  selectedEvolutions,
}: ChangeStatusPopupProps) => {
  const { t } = useTranslation("evolutions");
  const [selectedEvoState, setSelectedEvoState] =
    useState<EvolutionState | null>(null);
  const { updateEvolutionsStatus } = useSelector(evolutionsSelector);

  const hasSelection = selectedEvoState !== null;
  const dispatch = useDispatch<AppDispatch>();
  useScrollLock(true);

  const handleConfirm = () => {
    const evolutionStateItems: EvolutionStateItem[] = selectedEvolutions.map(
      (evo) => ({
        id: evo.id,
        categoryId: evo.categoryId,
        state: selectedEvoState,
      }),
    );
    dispatch(updateUserEvolutionStates({ evolutions: evolutionStateItems }));
  };

  const handleSelectedEvoState = (state: EvolutionState) => {
    if (selectedEvoState === state) {
      setSelectedEvoState(null);
    } else {
      setSelectedEvoState(state);
    }
  };

  useEffect(() => {
    const numberOfEvolutions = selectedEvolutions.length;
    if (updateEvolutionsStatus === "fulfilled") {
      const description = t("popups.changeStates.updated", {
        count: numberOfEvolutions,
      });
      onClose();
      onComplete();
      dispatch(
        addAlert({
          type: "success",
          description,
        }),
      );
      dispatch(resetUpdateEvolutionsStatus());
    } else if (updateEvolutionsStatus === "rejected") {
      const description = t("popups.changeStates.updateFailed", {
        count: numberOfEvolutions,
      });
      dispatch(
        addAlert({
          type: "error",
          description,
        }),
      );
      dispatch(resetUpdateEvolutionsStatus());
    }
  }, [updateEvolutionsStatus]);

  return (
    <BaseEvoPopup
      posLabel={t("popups.changeStates.title")}
      onPositive={() => {
        if (selectedEvoState) handleConfirm();
      }}
      posDisabled={!hasSelection || updateEvolutionsStatus === "pending"}
      handleOnClose={onClose}
      title={t("popups.changeStates.title")}
      imageUrl={
        import.meta.env.VITE_APP_ASSETS_BASE_URL +
        `/${PINNED_ASSET_SLUG}/evolutions-hub/change-state-v2.png`
      }
    >
      <>
        <p className="italic">
          {t("popups.changeStates.willBeUpdated", {
            count: selectedEvolutions?.length ?? 0,
          })}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {evoStateItems.map((opt) => (
            <Button
              key={opt.state}
              variant="secondary"
              size="medium"
              selected={selectedEvoState === opt.state}
              icon={getEvoStateIcon(opt.state, 16)}
              className="justify-start"
              onClick={() => handleSelectedEvoState(opt.state)}
            >
              {t(opt.labelKey)}
            </Button>
          ))}
        </div>
        <p>
          <Trans
            ns="evolutions"
            i18nKey="popups.changeStates.revertHint"
            components={{ i: <span className="italic" /> }}
          />
        </p>
      </>
    </BaseEvoPopup>
  );
};
